import {
  toastType,
  EMPTY_STRING,
  globalConstants,
  outletConstants,
  inventoryConstants,
  transactionConstants,
  toggleSwitchButtonConstants,
  SALE_TRANSACTION_ACTIVITY_TYPES,
  toastMessages,
  customerConstants,
  routesPathConstants,
  tradeTransactionDefaultData,
} from "../../constants";
import { transactionActions } from "../../redux/actions";
import { customerService } from "../../services";
import {
  PRODUCT_TYPES_ENUMS,
  STORE_CARD_PAYMENT_TYPE_ENUMS,
} from "../../system/globalEnums";
import {
  getItemFromLocalStorage,
  setItemToLocalStorage,
} from "../../system/storage";
import {
  calculateRatio,
  parseToDecimalNumber,
  getSumOfPaymentHistory,
  stringifyObject,
  parseJsonObject,
  convertToUsd,
  roundNumber,
  customToast,
  parseToNumber,
  createCompanyDynamicUrl,
  handleCalculateTotalCogsAndStockPrice,
  handleAddInventorySubmitBeautify,
  sum,
  getUserFullName,
  isSpinnerEnabled,
  getQuantityValue,
  getCogsValue,
  isTradeTransaction,
  parseToThousandSeperatorDecimalNumber,
  getSaleTaxRate,
  convertToFixedPrecision,
} from "../utility";

const getTradeItemLockStatus = (item, isTradeCart) =>
  isTradeCart
    ? !!item?.tradeProductMetaData?.isItemLocked
    : !!item?.isItemLocked;

const getTradeItemCashPercentage = (item, isTradeCart) =>
  isTradeCart
    ? Number(item?.tradeProductMetaData?.cashPercentagePerItem)
    : Number(item?.cashPercentagePerItem);

const getTradeItemTradePercentage = (item, isTradeCart) =>
  isTradeCart
    ? Number(item?.tradeProductMetaData?.tradePercentagePerItem)
    : Number(item?.tradePercentagePerItem);

export const getTradeItemRatio = (item, isTradeCart) =>
  calculateRatio(item, isTradeCart);

// lock or unlock the trade or cash offer
export const handleLockOrUnlockTradeItem = (
  currentStore,
  tradeData,
  setTradeData,
  itemId,
  isCartItem = false
) => {
  // Create a copy of tradeData to avoid direct mutation
  const updatedTradeData = [...(isCartItem ? tradeData?.inventory : tradeData)];

  // Find the item in the tradeData by its itemId
  const itemIndex = updatedTradeData.findIndex((item) => item.id === itemId);
  if (itemIndex !== -1) {
    // Toggle the lock status of the item
    const item = updatedTradeData[itemIndex];

    if (isCartItem) {
      item.tradeProductMetaData.isItemLocked =
        !item.tradeProductMetaData.isItemLocked;

      setTradeData(
        {
          ...tradeData,
          inventory: updatedTradeData,
        },
        currentStore?.id
      );
    } else {
      item.isItemLocked = !item.isItemLocked;
      // Update the tradeData with the new state
      setTradeData(updatedTradeData, currentStore?.id);
    }
  }
};

//------- get Total of CashOffer and Trade Offer
//------- in batch and trade cart
export const getTotalTradeOfferOfBatchProducts = (
  addTradeBatchInventory,
  isTradeCart = false,
  isConsiderLockCheck = true // if true then it will consider the lock check means if the item is lock (isItemLocked = true) then ignore and if not lock (isItemLocked = false) then consider the prodcut
) => {
  let totalCashOffer = 0;
  let totalTradeOffer = 0;

  addTradeBatchInventory.forEach((currentInv) => {
    const isItemLocked = getTradeItemLockStatus(currentInv, isTradeCart);
    const quantity = getQuantityValue(
      currentInv,
      isTradeCart,
      false,
      false,
      true
    );

    // Determine whether to count this item or not based on isCountLockedItem and currentInv.isItemLocked
    const shouldCount = isConsiderLockCheck
      ? !isItemLocked // Only count the item if it's not locked
      : true; // Count all items if isCountLockedItem is false

    // If the item should be counted, add its value to the total
    if (shouldCount) {
      totalCashOffer += Number(currentInv.cashOffer * quantity);
      totalTradeOffer += Number(currentInv.tradeOffer * quantity);
    }
  });
  return {
    cashOfferValue: parseToDecimalNumber(totalCashOffer),
    tradeOfferValue: parseToDecimalNumber(totalTradeOffer),
  };
};

//------- get Total of costOfGoods
//------- in batch and trade cart
export const getTotalCogsOfBatchProducts = (
  addTradeBatchInventory,
  isCountLockedItem = true
) => {
  let totalCostOfGoods = 0;

  addTradeBatchInventory.forEach((currentInv) => {
    const isItemLocked = currentInv.isItemLocked;
    const cogs = getCogsValue(currentInv, false, true);
    // Determine whether to count this item or not based on isCountLockedItem and currentInv.isItemLocked
    const shouldCount = isCountLockedItem
      ? !isItemLocked // Only count the item if it's not locked
      : true; // Count all items if isCountLockedItem is false

    // If the item should be counted, add its value to the total
    if (shouldCount) {
      totalCostOfGoods += Number(cogs);
    }
  });

  return parseToDecimalNumber(totalCostOfGoods);
};

// calculate cogs of processed transactions
export const getTotalCogsOrSubTotalOfProcessedTransaction = (
  data,
  isFormatValue = true,
  isSubTotal = false
) => {
  let totalValue = 0;
  if (data) {
    // in trade transaction we will always return SubTotal (for both variables: totalCogs, subTotal) to make sure we show the same value in the history table and view detail modal as subTotal is total Cogs but with split trades and large transaction when we recalculate each products cogs percentage there's a difference of few cents
    if (isTradeTransaction(data)) {
      totalValue = data?.SubTotal ?? 0;
    } else if (isSubTotal) {
      totalValue = data?.SubTotal ?? 0;
    } else {
      totalValue = data?.inventory?.reduce((sum, item) => {
        let productCogs = item?.cogs || item?.price?.unit_purchase_price || 0;
        return sum + (productCogs * item?.price?.quantity || 0);
      }, 0);
    }
  }
  if (isFormatValue) {
    totalValue = parseToThousandSeperatorDecimalNumber(totalValue || 0);
  }

  return totalValue || 0;
};

//-------- calculate percentage of cart item for both cash and trade
export const handleItemCashTradePercentage = (
  inventory,
  isTradeCart = false,
  isConsiderLockCheck = true,
  isProcessAllProducts = false // it make sure to calculate the percentage of each item including all products (locked & unlocked) because on the payment page user can split and on splitting we will calculate the cash offer and trade offer based on this percentage and then sum it and assign this value as a unit_purchase_price of product
) => {
  // Calculate the total cash offer
  const totalCashOffer =
    getTotalTradeOfferOfBatchProducts(
      inventory,
      isTradeCart,
      isConsiderLockCheck
    ).cashOfferValue ?? 0;

  // Calculate the total trade offer
  const totalTradeOffer =
    getTotalTradeOfferOfBatchProducts(
      inventory,
      isTradeCart,
      isConsiderLockCheck
    ).tradeOfferValue ?? 0;

  // Map through each inventory item to calculate and set both cash and trade percentages
  const updatedInventory = inventory.map((item, index) => {
    const quantity = getQuantityValue(item, isTradeCart, false, false, true);
    // Only calculate percentages for items that are not locked
    if (isProcessAllProducts || !getTradeItemLockStatus(item, isTradeCart)) {
      const itemTotalCashOffer = Number(item.cashOffer * quantity);
      const cashPercentagePerItem =
        totalCashOffer > 0 ? (itemTotalCashOffer / totalCashOffer) * 100 : 0;

      const itemTotalTradeOffer = Number(item.tradeOffer * quantity);
      const tradePercentagePerItem =
        totalTradeOffer > 0 ? (itemTotalTradeOffer / totalTradeOffer) * 100 : 0;

      if (isTradeCart) {
        return {
          ...item,
          tradeProductMetaData: {
            ...item.tradeProductMetaData,
            cashPercentagePerItem: parseToDecimalNumber(cashPercentagePerItem),
            tradePercentagePerItem: parseToDecimalNumber(
              tradePercentagePerItem
            ),
          },
        };
      } else {
        return {
          ...item,
          cashPercentagePerItem: parseToDecimalNumber(cashPercentagePerItem),
          tradePercentagePerItem: parseToDecimalNumber(tradePercentagePerItem),
        };
      }
    }

    // If the item is locked, return it without modifications
    return item;
  });

  return updatedInventory;
};

//-------get updated tardeOffer and CashOffer Value
export const getUpdatedOfferValues = (
  inputValue,
  isLink = true,
  isCashOfferValue,
  isTradeCart = true,
  updatedCartInventory,
  isLockedItemIncluded = true
) => {
  let recalulatePercentageForItems = handleItemCashTradePercentage(
    updatedCartInventory,
    isTradeCart,
    isLockedItemIncluded
  );

  // Step 6: Update the batch inventory based on whether it's a cash offer or trade offer
  let finalUpdatedBatchInventory = recalulatePercentageForItems.map((item) => {
    // Skip locked items (no need to update their offer values)
    if (getTradeItemLockStatus(item, isTradeCart)) {
      return item; // Return unchanged item for locked items
    }

    let offerValue = 0;
    // Default opposite offer value
    let oppositeOfferValue = !isCashOfferValue
      ? item.cashOffer
      : item.tradeOffer;

    if (isCashOfferValue) {
      // Calculate the cash offer for the item
      const quantity = getQuantityValue(item, isTradeCart, isTradeCart);
      offerValue =
        (inputValue * getTradeItemCashPercentage(item, isTradeCart)) /
        100 /
        quantity;
      // Apply the minimum threshold for cashOffer (if it falls below the threshold, set it to the default value)
      offerValue = parseToDecimalNumber(
        offerValue <= inventoryConstants.DEFAULT_OFFER_VALUE
          ? inventoryConstants.DEFAULT_OFFER_VALUE
          : offerValue
      );

      if (isLink) {
        // Calculate the corresponding trade offer based on the item ratio if isLink is true
        const itemRatio = getTradeItemRatio(item, isTradeCart);
        oppositeOfferValue = parseToDecimalNumber(
          itemRatio * parseToDecimalNumber(offerValue)
        );
      }
    } else {
      // Calculate the trade offer for the item
      const quantity = getQuantityValue(item, isTradeCart, isTradeCart);
      const tradeItemPercentage = getTradeItemTradePercentage(
        item,
        isTradeCart
      );
      offerValue = (inputValue * tradeItemPercentage) / 100 / quantity;
      // Apply the minimum threshold for tradeOffer
      offerValue = parseToDecimalNumber(
        offerValue <= inventoryConstants.DEFAULT_OFFER_VALUE
          ? inventoryConstants.DEFAULT_OFFER_VALUE
          : offerValue
      );
      if (isLink) {
        // Calculate the corresponding cash offer based on the item ratio if isLink is true
        const itemRatio = getTradeItemRatio(item, isTradeCart);
        oppositeOfferValue = parseToDecimalNumber(offerValue / itemRatio);
      }
    }
    return {
      ...item,
      cashOffer: isCashOfferValue
        ? parseToDecimalNumber(offerValue)
        : oppositeOfferValue,
      tradeOffer: isCashOfferValue
        ? parseToDecimalNumber(oppositeOfferValue)
        : offerValue,
    };
  });

  return finalUpdatedBatchInventory;
};

//------- offer values after slit payment
export const getSplitPaymentOfferValues = (
  inventory,
  remainingAmountToPay,
  newTradeData,
  response
) => {
  const paymentStatus =
    response.totalAmountToPay - getSumOfPaymentHistory(response.paymentHistory)
      ? EMPTY_STRING
      : transactionConstants.PAYMENT_COMPLETE;

  const isCashOfferValue = response.PaymentType === globalConstants.CASH;

  if (paymentStatus === EMPTY_STRING) {
    let reCalculatePercentages = handleItemCashTradePercentage(
      inventory,
      true,
      false
    );

    reCalculatePercentages = reCalculatePercentages.map((item) => {
      let offerValue = 0;
      let oppositeOfferValue = 0;

      // Calculate the cash offer for the item
      offerValue =
        (Number(response.currentAmountToPay) *
          Number(item.tradeProductMetaData.cashPercentagePerItem)) /
        100 /
        sum(item.price.quantity, item.price.onlineQuantity);

      // Apply the minimum threshold for cashOffer (if it falls below the threshold, set it to the default value)
      offerValue = parseToDecimalNumber(
        offerValue <= inventoryConstants.DEFAULT_OFFER_VALUE
          ? inventoryConstants.DEFAULT_OFFER_VALUE
          : offerValue
      );

      oppositeOfferValue =
        (Number(remainingAmountToPay) *
          Number(item.tradeProductMetaData.tradePercentagePerItem)) /
        100 /
        Number(item.price.quantity);

      // Apply the minimum threshold for cashOffer (if it falls below the threshold, set it to the default value)
      oppositeOfferValue = parseToDecimalNumber(
        oppositeOfferValue <= inventoryConstants.DEFAULT_OFFER_VALUE
          ? inventoryConstants.DEFAULT_OFFER_VALUE
          : oppositeOfferValue
      );

      return {
        ...item,
        cashOffer: isCashOfferValue
          ? parseToDecimalNumber(offerValue)
          : oppositeOfferValue,
        tradeOffer: isCashOfferValue
          ? parseToDecimalNumber(oppositeOfferValue)
          : offerValue,
      };
    });

    return reCalculatePercentages;
  }
  return inventory;
};

export const getLockCustomerPayload = (customer, currentStore, isLock) => {
  return {
    id: customer.id,
    companyId: currentStore.companyId,
    store: {
      id: currentStore.id,
      name: currentStore.storeName,
    },
    isCustomerInUseOfTrade: isLock,
  };
};

// if user is parking the transaction with guestCustomer => 1. Unlock guest customer 2. Remove customer from transaction 3. Set paymentType to cash (as if storeCredit then customer is required)
export const handleRemoveGuestCustomerBeforeTradeDraft = (
  transactionData,
  currentStore
) => {
  let updatedTransactionCustomer = {};
  let paymentType = transactionData.PaymentType;
  if (!transactionData.customer.id) {
    updatedTransactionCustomer = { id: EMPTY_STRING, name: EMPTY_STRING };
  } else if (transactionData.customer.isGuestCustomer) {
    updatedTransactionCustomer = { id: EMPTY_STRING, name: EMPTY_STRING };
    paymentType = globalConstants.CASH;
    customerService.lockCustomer(
      getLockCustomerPayload(transactionData.customer, currentStore, false)
    );
  } else {
    updatedTransactionCustomer = transactionData.customer;
  }

  return { customer: updatedTransactionCustomer, PaymentType: paymentType };
};

export const getSellTradeCustomerObject = (customer) => {
  return customer
    ? {
        firstName: customer.firstName,
        lastName: customer.lastName,
        email: customer.email,
        mobile: customer.mobile,
        companyCustomerId: customer.companyCustomerId,
        currentBalance: customer.currentBalance,
        store: {
          id: customer.store.id,
          name: customer.store.storeName,
        },
        id: customer.id,
        driverLicense: customer?.driverLicense,
        expirationDate: customer.expirationDate,
        name: `${customer.firstName} ${customer.lastName}`,
        driverLicenseIdType: customer?.driverLicenseIdType,
        isGuestCustomer: customer.isGuestCustomer ? true : false,
      }
    : EMPTY_STRING;
};

//------- User Activity Logs For Sale and Trade Transations
export const handleUserActivitiesOnSaleAndTrade = (
  activityName = "Add",
  user,
  newData,
  oldData,
  logType = transactionConstants.SALE_ACTIVITY_LOGS
) => {
  // Retrieve existing logs from localStorage, or initialize an empty object if none exist
  let userActivityLogs = parseJsonObject(
    getItemFromLocalStorage(transactionConstants.SALE_TRADE_ACTIVITY_LOGS)
  ) || {
    saleLogs: [],
    tradeLogs: [],
  };

  // Check if newData is different from oldData
  if (stringifyObject(newData) !== stringifyObject(oldData)) {
    // Create a log entry with relevant data
    const activityLog = {
      activityName: activityName,
      timestamp: new Date().toISOString(),
      user: `${user.firstName} ${user.lastName}`,
      oldData: oldData,
      newData: newData,
    };

    //------- select array to update in logs
    const logArray =
      logType === transactionConstants.SALE_ACTIVITY_LOGS
        ? userActivityLogs.saleLogs
        : logType === transactionConstants.TRADE_ACTIVITY_LOGS
        ? userActivityLogs.tradeLogs
        : null;

    //------ check is Transaction Complete or not
    const isPaymentComplete =
      newData?.paymentMethod === transactionConstants.PAYMENT_COMPLETE ||
      activityName === SALE_TRANSACTION_ACTIVITY_TYPES.RESET_TRANSACTION;

    if (logArray) {
      // If payment is complete, clear the log array, else push the log entry
      if (isPaymentComplete) {
        logArray.length = 0; // Clear the array efficiently
      } else {
        logArray.push(activityLog); // Add the log entry
      }
    }

    // Save the updated activity logs back to localStorage
    setItemToLocalStorage(
      transactionConstants.SALE_TRADE_ACTIVITY_LOGS,
      stringifyObject(userActivityLogs)
    );
  }
};

// This method is only called for sale transactions. All business-specific data mapping (custom item, sale custom item, inventory etc) should be handled within the component (addItemInCart), not here. This method only populates the required system values to prevent backend validation errors or values that are same in all cases
export const handleTransactionData = (
  data,
  user,
  currentStore,
  activityName,
  transactionData,
  setTransactionData
) => {
  let inventory = data.inventory;
  // Do not update the inventory for the following activity. handleTransactionData is also called from useEffect that depends on transactionData.inventory, so updating the inventory here would create a new array reference and cause the effect to run again, potentially resulting in an infinite render loop. Additionally, this activity does not modify any inventory values, so remapping the inventory is unnecessary.
  if (activityName !== SALE_TRANSACTION_ACTIVITY_TYPES.AMOUNT_CALCULATION) {
    inventory = data.inventory.map((inv) => {
      return {
        ...inv,
        quantity: inv.quantity,
        initialQuantity: inv.quantity,
      };
    });
  }
  setTransactionData(
    {
      ...data,
      inventory: inventory,
      processedBy: getUserFullName(user),
    },
    currentStore?.id
  );
  // handleUserActivitiesOnSaleAndTrade(
  //   activityName,
  //   user,
  //   data,
  //   transactionData,
  //   transactionConstants.SALE_ACTIVITY_LOGS
  // );
};

//------ handle Trade Transaction Data
export const handleTradeTransactionData = (
  data,
  user,
  currentStore,
  activityName,
  tradeData,
  setTradeData
) => {
  setTradeData(data, currentStore?.id);
  // handleUserActivitiesOnSaleAndTrade(
  //   activityName,
  //   user,
  //   data,
  //   tradeData,
  //   transactionConstants.TRADE_ACTIVITY_LOGS
  // );
};

//------ get product market price
export const getProductMarketPrice = (
  productDataObject,
  selectedRarity,
  isConvertToUsd = true
) => {
  if (selectedRarity) {
    return Number(productDataObject?.marketPrice ?? 0);
  } else {
    return isConvertToUsd
      ? Number(convertToUsd(productDataObject.marketPrice))
      : Number(productDataObject.marketPrice);
  }
};

// Return true if transaction(sale/trade) is a split transaction (some amount of total is paid).
export const isTransactionSplit = (transaction) => {
  return transaction?.paymentHistory?.history?.length >= 1;
};

export const getOutletRelationObjectForSaleTradePayload = (
  currentStore,
  currentOutlet
) => {
  return currentStore?.[toggleSwitchButtonConstants.IS_OUTLET_TRACKING] &&
    currentOutlet?.isCashTracking
    ? {
        id: currentOutlet?.id,
        name: currentOutlet?.name,
      }
    : {
        name: outletConstants.NOT_CONFIGURED,
        id: outletConstants.NOT_CONFIGURED,
      };
};

export const getSessionIdForSaleTradePayload = (
  currentStore,
  currentOutlet,
  currentSessionId
) => {
  return currentStore?.[toggleSwitchButtonConstants.IS_OUTLET_TRACKING] &&
    currentOutlet?.isCashTracking
    ? currentSessionId
    : outletConstants.NOT_CONFIGURED;
};

export const handleUnlockGuestCustomersInTrade = () => {
  const tradeReducerData = JSON.parse(
    getItemFromLocalStorage(transactionConstants.NEW_TRADE_PAGE_DATA)
  );
  if (
    tradeReducerData &&
    tradeReducerData.length &&
    tradeReducerData[0]?.data
  ) {
    let customersIdsArray = tradeReducerData
      .filter((trade) => {
        return (
          trade.data?.customer?.isGuestCustomer &&
          getSumOfPaymentHistory(trade.data?.paymentHistory) === 0
        );
      })
      .map((trade) => trade.data?.customer.id);

    customersIdsArray.length &&
      customerService.unlockCustomerInBulk(customersIdsArray);
  }
};

// Check if the ID type is "US Driver's License" and ID number is valid (non-empty)
export const getIndividualItemPaidTax = (
  quantity,
  itemPrice, //Discounted price as tax is applied after the discount
  taxPercentage
) => {
  return roundNumber((quantity * itemPrice * taxPercentage) / 100);
};

export const handleCalculateIndividualItemPriceStats = (dataToSend) => {
  let tempTax = 0;
  let tempDiscount = 0;
  let itemPaymentRatio = 0;
  let itemTotalPrice = 0;

  let updatedPaymentPaidHistory = {
    ...dataToSend.paymentHistory.totalPaidAmount,
    [dataToSend.PaymentType]:
      dataToSend.paymentHistory.totalPaidAmount[dataToSend.PaymentType] +
      dataToSend.currentAmountToPay,
  };

  let {
    cash: totalPaidCash,
    storeCredit: totalPaidStoreCredit,
    giftCard: totalPaidGiftCard,
    creditCard: totalPaidCreditCard,
    other: totalPaidOther,
  } = updatedPaymentPaidHistory;

  return dataToSend.inventory.map((inv) => {
    if (!dataToSend.isTaxApplied) {
      tempTax = 0;
    } else {
      const itemTaxPercentage = Number(
        inv?.taxPercentage ?? dataToSend.taxPercentage ?? 0
      );
      tempTax = getIndividualItemPaidTax(
        inv.quantity,
        inv.priceAfterGlobalDiscount,
        itemTaxPercentage
      );
    }

    tempDiscount = roundNumber(inv.actualPrice - inv.priceAfterGlobalDiscount);
    itemTotalPrice =
      Number(inv.priceAfterGlobalDiscount) * Number(inv.quantity);
    itemPaymentRatio = itemTotalPrice / Number(dataToSend.subTotal);

    if (
      dataToSend.TransactionType === transactionConstants.PROCESSED ||
      dataToSend.id.includes(globalConstants.TYPE_RETURN)
    ) {
      return {
        ...inv,
        ...getSaleInventoryPartialPayloadObject({
          ...inv,
          tax: tempTax,
          discount: tempDiscount,
          paymentHistory: {
            totalPaidAmount: {
              cash: roundNumber(totalPaidCash * itemPaymentRatio),
              storeCredit: roundNumber(totalPaidStoreCredit * itemPaymentRatio),
              creditCard: roundNumber(totalPaidCreditCard * itemPaymentRatio),
              giftCard: roundNumber(totalPaidGiftCard * itemPaymentRatio),
              other: roundNumber(totalPaidOther * itemPaymentRatio),
            },
          },
        }),
      };
    } else {
      return {
        ...inv,
        ...getSaleInventoryPartialPayloadObject({ ...inv }),
      };
    }
  });
};

export const isValidCustomerID = (customer) => {
  return (
    customer.driverLicenseIdType === customerConstants.ID_TYPES[0]?.value &&
    customer.driverLicense?.trim().length > 0
  );
};

//------handle trade pay click
export const handlePayClick = async (
  trasactionTypeCondition,
  currentStore,
  newTradeData,
  cartItemsCount,
  addTradeBatchInventory,
  outlet,
  newTradePark,
  navigate,
  resetTradePage,
  updateTradeData,
  transactionActions,
  user
) => {
  const isIdRequiredForTradeIn =
    currentStore[toggleSwitchButtonConstants.IS_ID_REQUIRED_TRADE_IN];
  const isIdRequiredForTradeInStoreCredit =
    currentStore[
      toggleSwitchButtonConstants.IS_ID_REQUIRED_TRADE_IN_STORE_CREDIT
    ];
  const hasCustomer = Boolean(newTradeData.customer?.id);
  const isCash = newTradeData.PaymentType === globalConstants.CASH;
  const isCredit = newTradeData.PaymentType === globalConstants.CREDIT;

  // Show error if no customer is selected and required by payment type
  if (
    (!hasCustomer && isCredit) ||
    (!hasCustomer && isCash && isIdRequiredForTradeIn)
  ) {
    customToast(toastMessages.PLEASE_SELECT_A_CUSTOMER, toastType.ERROR);
    return;
  }
  // Validate customer ID based on payment type and requirements
  if (
    hasCustomer &&
    ((!isValidCustomerID(newTradeData.customer) &&
      isCash &&
      isIdRequiredForTradeIn) ||
      (!isValidCustomerID(newTradeData.customer) &&
        isCredit &&
        isIdRequiredForTradeInStoreCredit))
  ) {
    customToast(toastMessages.VALID_CUSTOMER_REQUIRED, toastType.ERROR);
    return;
  }

  // handle component unmounting
  if (cartItemsCount) {
    cartItemsCount.current = 0;
  }
  // let isInvalid = newTradeData?.inventory?.some((inv) => {
  //   return Number(inv.cashOffer) === 0 || Number(inv.tradeOffer) === 0;
  // });

  // if (isInvalid) {
  //   customToast("Trade Offer & Cash Offer Cannot be 0", toastType.ERROR);
  //   return;
  // }

  if (
    newTradeData?.inventory?.length > 0 ||
    (addTradeBatchInventory.length &&
      trasactionTypeCondition === transactionConstants.DRAFT)
  ) {
    newTradeData.inventory.forEach((item) => {
      const offerValue =
        newTradeData.PaymentType === globalConstants.CASH
          ? item.cashOffer
          : item.tradeOffer;
      if (item.price) {
        item.price.unit_purchase_price = offerValue;
      } else {
        item.price = { unit_purchase_price: offerValue };
      }
    });
    const { initialTotalCogs, initialTotalStockPrice } =
      handleCalculateTotalCogsAndStockPrice(
        newTradeData.inventory,
        true,
        convertToFixedPrecision(getSubtotal(newTradeData))
      );

    const dataToSend = {
      ...newTradeData,
      userPaymentType: newTradeData.PaymentType,
      customer: newTradeData.customer.id
        ? newTradeData.customer
        : { id: "", name: "" },
      store: currentStore || "",
      outlet: getOutletRelationObjectForSaleTradePayload(
        currentStore,
        outlet.currentOutlet
      ),
      sessionId: getSessionIdForSaleTradePayload(
        currentStore,
        outlet.currentOutlet,
        outlet.currentSessionId
      ),
      paymentMethod: EMPTY_STRING,
      totalCashOffer: newTradeData?.totalCashOffer,
      totalTradeOffer: newTradeData?.totalTradeOffer,
      amountToPay: parseToNumber(newTradeData.amountToPay),
      initialTotalCogs: initialTotalCogs,
      initialTotalStockPrice: initialTotalStockPrice,
      processedBy: `${user.firstName} ${user.lastName}`,
    };
    if (trasactionTypeCondition === transactionConstants.DRAFT) {
      const tradeId =
        newTradeData?.id || (await transactionActions.getTradeId());

      const resOfRemoveGuestCustomer =
        handleRemoveGuestCustomerBeforeTradeDraft(newTradeData, currentStore);

      const parkTradeData = {
        ...dataToSend,
        id: tradeId,
        store: { id: currentStore?.id, name: currentStore?.storeName },
        customer: resOfRemoveGuestCustomer.customer,
        PaymentType: resOfRemoveGuestCustomer.PaymentType,
        userPaymentType: resOfRemoveGuestCustomer.PaymentType,
        addBatchInventory: addTradeBatchInventory,
        initialTotalCogs: initialTotalCogs,
        initialTotalStockPrice: initialTotalStockPrice,
      };
      newTradePark(parkTradeData, currentStore?.id);
      resetTradePage();
    } else {
      updateTradeData(
        dataToSend,
        SALE_TRANSACTION_ACTIVITY_TYPES.PAYMENT_BUTTON_CLICK
      );
      navigate(
        createCompanyDynamicUrl(
          routesPathConstants.SELL_AND_TRADE_NEW_TRADE_PAYMENT
        ),
        {
          state: {
            currentStore: currentStore,
          },
        }
      );
    }
  } else {
    customToast(toastMessages.PLEASE_SELECT_SOME_PRODUCT, toastType.ERROR);
  }
};

export const handleIsCustomItem = (productObj) => {
  return productObj?.tags.includes(inventoryConstants.CUSTOM_ITEM_TAG);
};

export const getSaleInventoryPartialPayloadObject = (values) => {
  return {
    tax: values?.tax || 0,
    discount: values?.discount || 0,
    grade: values?.grade || EMPTY_STRING,
    barcode: values?.barcode || EMPTY_STRING,
    paymentHistory:
      values?.paymentHistory || tradeTransactionDefaultData.paymentHistory,
  };
};

//------Split Values Calculation for Trade and Cash Offer
export const splitCalculation = (
  value,
  isCashPayment,
  newTradeData,
  isOtherAmount = false
) => {
  const totalOffer = isCashPayment
    ? newTradeData.totalTradeOffer
    : newTradeData.totalCashOffer;
  const otherOffer = isCashPayment
    ? newTradeData.totalCashOffer
    : newTradeData.totalTradeOffer;

  if (isOtherAmount) {
    const percentage = (value * 100) / otherOffer;
    const adjustedPercentage = (totalOffer * percentage) / 100;
    return totalOffer - adjustedPercentage;
  } else {
    const percentage = (value * 100) / totalOffer;
    const adjustedPercentage = (otherOffer * percentage) / 100;
    return otherOffer - adjustedPercentage;
  }
};

//-------Get Purchase Price of Trade Item
export const getPurchasePriceForTradeItem = (
  isSplitPayment,
  isCashPayment,
  cartItem
) => {
  if (isSplitPayment) {
    return cartItem?.price?.unit_purchase_price;
  } else {
    if (isCashPayment) {
      return cartItem?.cashOffer;
    } else {
      return cartItem?.tradeOffer;
    }
  }
};

//-------handle purchase price of trade item on split value change
export const getPurchasePriceAfterSplitValueChange = (
  totalCashOffer,
  totalTradeOffer,
  inventory
) => {
  return inventory.map((item) => {
    let cashOfferValue =
      (totalCashOffer * item.tradeProductMetaData.cashPercentagePerItem) /
      (100 *
        (item.isSeparateQuantity
          ? item.price.onlineQuantity
          : item.price.quantity));
    let tradeOfferValue =
      (totalTradeOffer * item.tradeProductMetaData.tradePercentagePerItem) /
      (100 *
        (item.isSeparateQuantity
          ? item.price.onlineQuantity
          : item.price.quantity));

    return {
      ...item,
      price: {
        ...item.price,
        unit_purchase_price: parseToDecimalNumber(
          cashOfferValue + tradeOfferValue
        ),
      },
    };
  });
};

//--------Get Trade Transaction Details
export const getTradeTransactionDetails = async (
  data,
  isConsent,
  paymentType,
  activateSpinner,
  transactionType,
  deactivateSpinner,
  isOtherSplitPayment = false
) => {
  const {
    id,
    tax,
    store,
    SubTotal,
    customer,
    amountToPay,
    paymentHistory,
    totalAmountToPay,
  } = data;
  const inventory = handleAddInventorySubmitBeautify(data.inventory);
  // Calculate cart quantity for trade
  const cartQuantity =
    inventory?.reduce((sum, item) => {
      const qty =
        Number(item.price?.quantity || 0) +
        Number(item.price?.onlineQuantity || 0);
      return sum + (qty || 1);
    }, 0) || 0;
  //-------get tradeId if not found
  let tradeId = id;
  if (tradeId === undefined || tradeId === null || tradeId === "") {
    activateSpinner(transactionConstants.GET_TRADE_ID);
    tradeId = await transactionActions.getTradeId();
    deactivateSpinner(transactionConstants.GET_TRADE_ID);
  }

  //-------prepare data object for transaction
  let updatedData = {
    ...data,
    Tax: tax,
    id: tradeId,
    creditUsed: 0,
    SubTotal: SubTotal,
    customer: customer,
    isConsent: isConsent,
    inventory: inventory,
    PaymentType: paymentType,
    isOtherSplitPayment: isOtherSplitPayment,
    paymentHistory: paymentHistory,
    TransactionType: transactionType,
    initialTotalCogs: SubTotal,
    cartQuantity: cartQuantity,
    totalAmountToPay: totalAmountToPay,
    store: {
      id: store?.id,
      name: store?.storeName || store?.name,
    },
    currentAmountToPay: Number(Number(amountToPay).toFixed(2)),
  };
  const { splitAmounts } = data;

  if (paymentType === transactionConstants.SPLIT) {
    const { cashAmount, tradeAmount } = splitAmounts || {};

    let newPaymentType = paymentType;
    if (cashAmount <= 0) {
      newPaymentType = globalConstants.CREDIT;
    } else if (tradeAmount <= 0) {
      if (isOtherSplitPayment) {
        newPaymentType = globalConstants.OTHER;
      } else {
        newPaymentType = globalConstants.CASH;
      }
    }

    updatedData = {
      ...updatedData,
      inventory: getPurchasePriceAfterSplitValueChange(
        cashAmount,
        tradeAmount,
        updatedData?.inventory
      ),
      totalCashOffer: cashAmount,
      totalTradeOffer: tradeAmount,
      PaymentType: newPaymentType,
    };
  }

  return updatedData;
};

// === Helper: Get adjusted payment amount based on type ===
export const getAdjustedPaymentAmount = (
  paymentType,
  transactionData,
  giftCardDetails
) => {
  if (paymentType === transactionConstants.GIFT_CARD) {
    return Math.min(
      giftCardDetails.giftCardBalance,
      parseToNumber(transactionData.currentAmountToPay)
    );
  }

  if (paymentType === transactionConstants.STORE_CREDIT) {
    return Math.min(
      parseToNumber(transactionData?.customer?.currentBalance),
      parseToNumber(transactionData.currentAmountToPay)
    );
  }

  return parseToNumber(transactionData.currentAmountToPay);
};

// === Helper: Build gift card details object ===
export const getGiftCardDetailsObject = (giftCardDetails, amount) => {
  if (!giftCardDetails) return {};

  return {
    giftCardName: `Gift Card(...${giftCardDetails.giftCardBarcode?.slice(-4)})`,
    giftCardChargeAmount: amount,
    giftCardBarcode: giftCardDetails.giftCardBarcode,
    giftCardTotalBalance: giftCardDetails.giftCardBalance,
    remainingBalance: giftCardDetails.giftCardBalance - amount,
  };
};

// === Helper: Recalculate totals if pre-tax logic is applied ===
export const recalculatePreTaxTotals = (
  data,
  newPaymentAmount,
  isFirstPayment
) => {
  if (Number(data.currentAmountToPay) >= newPaymentAmount) {
    data.currentAmountToPay = newPaymentAmount;
    data.TransactionType = transactionConstants.PROCESSED;
    data.Tax = 0;
    data.taxPercentage = 0;
    data.totalAmountToPay = newPaymentAmount;
    // if (data.giftCard && data.PaymentType === transactionConstants.GIFT_CARD) {
    //   data.giftCard.giftCardChargeAmount = newPaymentAmount;
    //   data.giftCard.remainingBalance =
    //     data.giftCard.giftCardTotalBalance - newPaymentAmount;
    // }
  } else if (data.isTaxApplied) {
    // recalculate subtotal
    let reTotal = data.inventory?.reduce((totalPrice, item) => {
      return (
        totalPrice + parseToNumber(item.priceAfterDiscount) * item.quantity
      );
    }, 0);

    // Find gift card value (after discount applied)
    let giftCardTotal = data.inventory?.reduce((total, item) => {
      if (item.category === PRODUCT_TYPES_ENUMS.GIFT_CARD) {
        return total + parseToNumber(item.priceAfterDiscount) * item.quantity;
      }
      return total;
    }, 0);

    // proportionally apply discount on gift card value
    let giftCardAfterDiscount = giftCardTotal;
    if (data.isDiscountField && reTotal > 0) {
      let discountRatio = data.discount / reTotal;
      giftCardAfterDiscount = giftCardTotal - giftCardTotal * discountRatio;
    }

    let taxableAmount = newPaymentAmount - giftCardAfterDiscount;

    const newSubTotal = taxableAmount - Number(data.currentAmountToPay);
    const newTax = roundNumber((newSubTotal * data.taxPercentage) / 100);

    data.Tax = newTax;
    data.totalAmountToPay = newPaymentAmount + newTax;
  }

  return data;
};

// === Helper: Construct final payload ===
export const buildTransactionPayload = ({
  user,
  transactionId,
  paymentType,
  currentAmountToPay,
  giftCard,
  transactionData,
  currentStore,
  currentOutlet,
  poiId,
  amountTendered,
  changeOwed,
  currentSessionId,
  otherAmountTendered,
}) => {
  const customer = transactionData.customer || { name: "", id: "" };
  const paymentHistory =
    transactionData?.paymentHistory ||
    transactionConstants.defaultPaymentHistory;

  const TransactionType =
    parseToNumber(
      currentAmountToPay + getSumOfPaymentHistory(paymentHistory)
    ) === parseToNumber(transactionData?.totalAmountToPay)
      ? transactionConstants.PROCESSED
      : transactionConstants.DRAFT;

  return {
    creditUsed: 0,
    id: transactionId,
    giftCard,
    customer,
    PaymentType: paymentType,
    note: transactionData.note,
    TransactionType,
    companyId: currentStore.companyId,
    SubTotal: transactionData?.subTotal,
    inventory: transactionData.inventory,
    currentAmountToPay,
    creditCard: transactionData.creditCard,
    discount: transactionData.discount || 0,
    isNoteField: transactionData.isNoteField,
    cashChangeOwed: parseToNumber(changeOwed),
    isTaxApplied: transactionData.isTaxApplied,
    poiId: poiId || "",
    activeCardPaymentType:
      paymentType === transactionConstants.CREDIT_CARD
        ? currentStore.activeCardPaymentType
        : STORE_CARD_PAYMENT_TYPE_ENUMS.NULL,
    discountMethod: transactionData.discountMethod,
    Tax: parseToDecimalNumber(transactionData?.tax),
    taxPercentage: transactionData?.taxPercentage ?? currentStore?.defaultTax,
    discountedValue: transactionData.discountedValue,
    isDiscountField: transactionData.isDiscountField,
    cartQuantity: transactionData?.cartQuantity || 0,
    cashTenderedAmount: parseToNumber(amountTendered),
    totalAmountToPay: transactionData?.totalAmountToPay,
    paymentHistory,
    initialTotalCogs: transactionData?.initialTotalCogs,
    initialTotalStockPrice: transactionData?.initialTotalStockPrice,
    store: {
      id: transactionData?.store?.id,
      name: transactionData?.store?.storeName || transactionData?.store?.name,
    },
    outlet: getOutletRelationObjectForSaleTradePayload(
      currentStore,
      currentOutlet
    ),
    sessionId: getSessionIdForSaleTradePayload(
      currentStore,
      currentOutlet,
      currentSessionId
    ),
    macAddress: currentOutlet?.macAddress,
    processedBy: `${user.firstName} ${user.lastName}`,
    otherAmountTendered: otherAmountTendered,
  };
};

export const getDraftTransactionType = (id) => {
  return id.substring(0, 3) === globalConstants.TYPE_ID_TRD
    ? transactionConstants.TRADE
    : id.substring(0, 3) === globalConstants.TYPE_ID_TRA
    ? transactionConstants.SALE
    : transactionConstants.BATCH;
};

export const isTransactionInProgress = (spinnerArray) => {
  return (
    isSpinnerEnabled(spinnerArray, transactionConstants.GET_RETURN_ID) ||
    isSpinnerEnabled(spinnerArray, transactionConstants.MAKE_TRANSACTION)
  );
};

export const isTradeInProgress = (spinnerArray) => {
  return isSpinnerEnabled(spinnerArray, [
    transactionConstants.MAKE_TRADE_TRANSACTION,
    transactionConstants.GET_TRADE_ID,
  ]);
};

export const isReturnInProgress = (spinnerArray) => {
  return isSpinnerEnabled(spinnerArray, [
    transactionConstants.ADD_RETURN,
    transactionConstants.GET_RETURN_ID,
  ]);
};

export const calculateItemTax = (item, currentStore, taxTypes) => {
  const { saleTaxRate } = getSaleTaxRate(
    taxTypes,
    item.productType,
    item,
    currentStore
  );
  return saleTaxRate;
};

// validate sale, trade, return, batch transaction id (fix error on searching ids with special characters on return screen)
export const isValidTransactionId = (transactionId) => {
  const stringifyId = String(transactionId).toUpperCase();
  return (
    // must start with id initials
    (stringifyId.startsWith(globalConstants.TYPE_ID_TRA) ||
      stringifyId.startsWith(globalConstants.TYPE_ID_TRD) ||
      stringifyId.startsWith(globalConstants.TYPE_RETURN)) &&
    // ' or "" is not allowed in transaction id search
    !stringifyId.includes("'") &&
    !stringifyId.includes('"')
  );
};

export const getSubtotal = (tradeData) => {
  if (Array.isArray(tradeData?.inventory) && tradeData?.inventory.length > 0) {
    return tradeData?.inventory.reduce((acc, item) => {
      const offer =
        tradeData.PaymentType === globalConstants.CASH ||
        tradeData.PaymentType === globalConstants.OTHER
          ? item.cashOffer
          : item.tradeOffer;
      const quantity = item.trackQuantity ? item.price?.quantity : 1;
      const onlineQuantity = item.trackQuantity
        ? item.price?.onlineQuantity
        : 1;
      return acc + offer * (quantity || 0) + offer * (onlineQuantity || 0);
    }, 0);
  }
  return 0;
};
